<template>
  <v-container>
    <v-row justify="center" class="h-100">
      <v-sheet class="bg-background flex-1-1 pa-4" max-width="600">
        <UserEditComponent
          :user="user"
          :is-admin="true"
          :is-create="false"
          @profile-saved="handleSave"
          @snack="throwSnack">
        </UserEditComponent>
      </v-sheet>
    </v-row>
    <v-row>
      <v-col cols="12" md="6" class="d-flex justify-center">
        <v-btn color="secondary" @click="openDeleteModal">Delete</v-btn>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-center">
        <v-btn color="secondary" @click="showResetPasswordModal = true">Reset Password</v-btn>
      </v-col>
    </v-row>
    <ConfirmDeleteComponent
      :description="`${user?.first_name} ${user?.last_name}`"
      item="User"
      :isOpen="showDeleteModal"
      @actionCanceled="closeDeleteModal"
      @actionConfirmed="deleteUserAndCloseModal">
    </ConfirmDeleteComponent>

    <!-- Reset Password Modal -->
    <v-dialog v-model="showResetPasswordModal">
      <UserPasswordResetComponent
        :user="user"
        :is-admin="authStore.isAdmin"
        :allow-close="true"
        @password-reset-canceled="showResetPasswordModal = false"
        @password-reset-completed="showResetPasswordModal = false"
        @snack="throwSnack">
      </UserPasswordResetComponent>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSnack } from '@/helpers';
import { IUserDTO, UserDTO } from '@/dtos';
import { SNACKSTATUS } from '@/stores/util';
import { useAuthStore, useUpdateStatusStore, useUserStore } from '@/stores/modules';
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';
import UserEditComponent from '@/components/user/UserEditComponent.vue';
import UserPasswordResetComponent from '@/components/user/UserPasswordResetComponent.vue';
const router = useRouter();
// Get userId from route params
const route = useRoute();
const userId = ref(Array.isArray(route.params.id) ? route.params.id[0] : (route.params.id ?? ''));
const userStore = useUserStore();
const authStore = useAuthStore();
const statusStore = useUpdateStatusStore();
const { data: getUserQuery } = userStore.getUserByIdQuery(userId);
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);
// you can now safely access .data in a computed
const user = ref<IUserDTO>(new UserDTO());
const showDeleteModal = ref<boolean>(false);
const showResetPasswordModal = ref<boolean>(false);

const handleSave = (saved: boolean) => {
  if (saved) {
    router.push({ name: 'AdminUserListing' });
  }
};

watch(
  () => statusStore.getStatus(`user-delete-${user.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `user-delete-${user.value.id}`;
    emit('snack', 'User', value, statusStore.getError(key));
    statusStore.clearStatus(key);
    router.push({ name: 'AdminUserListing' });
  },
);
watch(
  () => getUserQuery.value,
  () => {
    user.value = new UserDTO(getUserQuery.value);
  },
  { immediate: true, deep: true },
);

// Modal handlers
const openDeleteModal = () => (showDeleteModal.value = true);
const closeDeleteModal = () => (showDeleteModal.value = false);
const deleteUserAndCloseModal = async () => {
  userStore.remove.mutateAsync(userId.value);
  closeDeleteModal();
};
</script>
